import { type DocumentNode, print } from "graphql";

import {
  GRAPHQL_CLIENT_NAME,
  GRAPHQL_CLIENT_VERSION,
  LOCALE_REQUEST_HEADER_KEY,
  STORE_REQUEST_HEADER_KEY,
} from "@/api/constants";
import type { Locale } from "@/i18n/i18n-config";
import { DD_ENV, DD_VERSION } from "@/modules/config";
import { refreshAccessToken } from "@/modules/lib/auth";
import { CookieKeys } from "@/utils/constants";
import { deleteCookie } from "@/utils/cookie";

export type GraphqlError = {
  message: string;
  code?: string;
  extensions?: {
    code?: string;
  };
};

export const fetchGraphql = async <Response, Variables>({
  url,
  query,
  variables,
  locale,
  store,
  headers = {},
  next = {},
  credentials,
  shouldHandleRefreshToken = false,
}: {
  url: string;
  query: DocumentNode;
  variables?: Variables;
  locale?: Locale;
  store: string;
  headers?: Record<string, string>;
  ssrAccessToken?: string;
  next?: NextFetchRequestConfig;
  credentials?: RequestCredentials;
  shouldHandleRefreshToken?: boolean;
}): Promise<Response> => {
  const makeRequest = async (): Promise<Response> => {
    const res = await fetch(url, {
      method: "POST",
      next,
      ...(credentials && { credentials }),
      headers: {
        "content-type": "application/json",
        [STORE_REQUEST_HEADER_KEY]: store,
        [LOCALE_REQUEST_HEADER_KEY]: locale ?? "",
        [GRAPHQL_CLIENT_NAME]: "pos-ui",
        [GRAPHQL_CLIENT_VERSION]: `${DD_ENV}-${DD_VERSION}`,
        ...headers,
      },
      body: JSON.stringify({ query: print(query), variables }),
    });

    const json = await res.json();
    if (
      shouldHandleRefreshToken &&
      json.errors &&
      json.errors.some(
        (error: GraphqlError) => error.code === "UNAUTHENTICATED",
      )
    ) {
      try {
        await refreshAccessToken();
        return makeRequest();
      } catch {
        // Refresh token failed, delete cookies which will log out the user
        await Promise.all([
          deleteCookie(CookieKeys.AuthCookie),
          deleteCookie(CookieKeys.AuthRefreshCookie),
        ]);
        // Take user to the login page
        window.location.href = "/";
      }
    }

    if (json.errors) {
      const errors = json.errors.map((error: GraphqlError) => {
        const { message, code, extensions } = error;
        return {
          message,
          code: extensions?.code ?? code,
        };
      });

      throw errors;
    }
    return json.data;
  };

  return makeRequest();
};
